import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { get, doDelete } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import { GrGallery, GrDocumentText, GrCatalogOption } from "react-icons/gr";

function getTypeIcon(t) {
  const type = (t || '').toLowerCase();
  switch (type) {
    case 'article':
      return <GrDocumentText size={14} aria-label="article" />;
    case 'catalog':
      return <GrCatalogOption size={14} aria-label="catalog" />;
    case 'gallery':
    default:
      return <GrGallery size={14} aria-label='gallery' />;
  }
}

function Structure({ website /*, onUpdated */ }) {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadPages() {
      if (!website?.id) return;
      try {
        setLoading(true);
        const resp = await get(`websites/${website.id}/pages`);
        const list = Array.isArray(resp) ? resp : (resp?.pages || []);
        if (isMounted) setPages(list);
      } catch (e) {
        if (isMounted) toast.error('Failed to load pages.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadPages();
    return () => { isMounted = false; };
  }, [website?.id]);

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const goToCreate = () => {
    navigate(`/websites/${website.id}/structure/new`);
  };

  const goToEdit = (id) => {
    navigate(`/websites/${website.id}/structure/${id}/edit`);
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const addEntity = (parentType = null, parentId = null) => {
    let parentParam = parentId ? `?parent=${encodeURIComponent(parentId)}` : '';
    if (parentType.toLowerCase() === 'gallery') {
       parentParam += '&type=gallery-item';
    }
    navigate(`/websites/${website.id}/structure/new${parentParam}`);
  };

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      await doDelete(`websites/${website.id}/pages/${id}`);
      setPages(prev => prev.filter(p => p.id !== id));
      setConfirmingId(null);
      toast.success('Page deleted.');
    } catch (e) {
      toast.error('Failed to delete page.');
    } finally {
      setDeleting(false);
    }
  };

  function StructureTree({ pages, expanded, onToggle, onEdit, onAdd }) {
    const keyFor = (parent) => (parent === null || parent === undefined || parent === '' ? '__root__' : parent);
    const pagesByParent = useMemo(() => {
      const map = {};
      for (const pg of pages || []) {
        const k = keyFor(pg.parent);
        if (!map[k]) map[k] = [];
        map[k].push(pg);
      }
      Object.values(map).forEach(arr => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id)));
      return map;
    }, [pages]);

    const NodeRow = ({ p, level }) => {
      const type = (p.type || '').toLowerCase();
      const isBranch = type === 'catalog' || type === 'gallery';
      const hasChildren = (pagesByParent[keyFor(p.id)] || []).length > 0;
      const isOpen = !!expanded[p.id];
      const isConfirming = confirmingId === p.id;

      return (
        <div className="structure-node">
          <div className="structure-node-header">
            <div className="structure-node-info">
              <div style={{ width: level * 14 }} aria-hidden />
              {isBranch ? (
                <button
                  type="button"
                  onClick={() => onToggle(p.id)}
                  title={isOpen ? 'Collapse' : 'Expand'}
                  aria-label={isOpen ? 'Collapse children' : 'Expand children'}
                  className="icon-btn"
                >
                  {getTypeIcon(p.type)}
                </button>
              ) : (
                <span>{getTypeIcon(p.type)}</span>
              )}
              <span className="structure-node-slug">/{p.slug}</span>
              <span className="structure-node-title">{p.title}</span>
            </div>
            <div className="structure-node-info">
              {isConfirming ? (
                <>
                  <span className="structure-delete-label">Delete?</span>
                  <button
                    type="button"
                    className="structure-confirm-btn structure-confirm-btn--yes"
                    onClick={() => handleDelete(p.id)}
                    disabled={deleting}
                  >
                    {deleting ? <Spinner animation="border" size="sm" /> : 'Yes'}
                  </button>
                  <button
                    type="button"
                    className="structure-confirm-btn structure-confirm-btn--no"
                    onClick={() => setConfirmingId(null)}
                    disabled={deleting}
                  >
                    No
                  </button>
                </>
              ) : (
                <>
                  <Button size="sm" className="primary-button m-0" onClick={() => onEdit(p.id)}>Edit</Button>
                  {isBranch && (
                    <Button size="sm" className="primary-button m-0" onClick={() => onAdd(p.type, p.id)}>Add</Button>
                  )}
                  <button
                    type="button"
                    className="structure-delete-btn"
                    title="Delete page"
                    onClick={() => setConfirmingId(p.id)}
                  >×</button>
                </>
              )}
            </div>
          </div>
          {isBranch && isOpen && hasChildren && (
            <div className="structure-node-children">
              <TreeLevel parentSlug={p.id} level={level + 1} />
            </div>
          )}
        </div>
      );
    };

    const TreeLevel = ({ parentSlug, level = 0 }) => {
      const k = keyFor(parentSlug);
      const nodes = pagesByParent[k] || [];
      if (nodes.length === 0) return null;
      return (
        <div>
          {nodes.map((p) => (
            <NodeRow key={p.slug} p={p} level={level} />
          ))}
        </div>
      );
    };

    return <TreeLevel parentSlug={null} level={0} />;
  }

  return (
    <div>
      <div className="mt-3">
        <h5>Pages</h5>
        <p className="muted-hint mt-1">
          Manage your site's pages. Use the tree below to navigate and edit.
        </p>
        <Button className="primary-button mb-3" size="sm" onClick={goToCreate}>Add</Button>
        {loading ? (
          <div className="text-center my-3"><Spinner animation="border" size="sm" /></div>
        ) : pages.length === 0 ? (
          <div className="structure-empty">
            <div className="structure-empty-icon"><GrDocumentText /></div>
            <div className="structure-empty-title">No pages yet</div>
            <div className="structure-empty-hint">Use the Add button to create your first content entity.</div>
          </div>
        ) : (
          <StructureTree
            pages={pages}
            expanded={expanded}
            onToggle={toggleExpand}
            onEdit={goToEdit}
            onAdd={addEntity}
          />
        )}
      </div>
    </div>
  );
}

export default Structure;
